import { useTheme } from '@mui/material/styles';
import type { EditorOptions, EditorEvents } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import { useEffect } from 'react';
import {
  getTableBlockExtensionsForEditor,
  getTableBlockExtensionsForPreview,
  getTableBlockExtensionsForPreviewContent,
  setTipTapEditorConfig,
} from '../helpers/config';

import type { ImageUploadOptions, ITextEditorOption } from '../types';
import type { VariableEntity } from '../../../../../services/entities/VariablesEntity';
import { useAIToken } from '../TipTapBarMenu/AI';
export type TextEditorProps = {
  placeholder?: string;
  blockId?: string;
  onChange?: (blockId: string, value: string) => void;
  value?: string;
  isTemplate?: boolean;
  tab: 'editor' | 'preview';
  user?: ITextEditorOption;
  mentions?: ITextEditorOption[];
  userPathname?: string;
  uploadFileOptions?: Omit<ImageUploadOptions, 'type'>;
  variables?: VariableEntity[];
  documentCurrencyCode?: string;
  disableSuggestions?: boolean;
} & Partial<EditorOptions>;

export const useTextEditorForTableBlock = ({
  blockId,
  onChange,
  value,
  tab,
  editable = true,
  isTemplate = false,
  variables = [],
  documentCurrencyCode,
  disableSuggestions = false,
  ...editorOptions
}: TextEditorProps) => {
  const theme = useTheme();
  const { token } = useAIToken(tab === 'preview');
  const editor = useEditor(
    {
      content: value,
      extensions:
        tab === 'preview'
          ? !isTemplate
            ? getTableBlockExtensionsForPreview({ variables, documentCurrencyCode })
            : getTableBlockExtensionsForPreviewContent({ variables, documentCurrencyCode })
          : getTableBlockExtensionsForEditor({ variables, jwtToken: token, documentCurrencyCode, disableSuggestions }),
      onUpdate: ({ editor }: EditorEvents['update']) => {
        if (blockId) {
          const html = editor.getHTML();
          onChange?.(blockId, html);
        }
      },
      ...editorOptions,
    },
    [token]
  );

  useEffect(() => {
    if (!editor) return;
    if (value && editor.isEmpty) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  useEffect(() => {
    setTipTapEditorConfig(editor, editable, tab);
  }, [editor, tab, editable, theme]);

  return editor;
};
